import { Box, Popover, Stack, SxProps, Theme, Typography } from '@mui/material';
import React, { ReactNode, useMemo, useState } from 'react';

export type TagColor = 'red' | 'blue' | 'green' | 'purple' | 'orange' | 'yellow' | 'grey';

export interface TagPillProps {
  color?: TagColor;
  autocolor?: number;
  text: string;
  icon?: ReactNode;
  trailingIcon?: ReactNode;
  sx?: SxProps<Theme>;
  hoverContent?: ReactNode;
  onClick?: (event: React.MouseEvent) => void;
  emphasize?: boolean;
  maxWidth?: string | number;
  ellipsis?: boolean;
}

const colorMap: Record<TagColor, { bg: string, text: string }> = {
  red: { bg: '#FEE2E2', text: '#B91C1C' },
  blue: { bg: '#DBEAFE', text: '#1E40AF' },
  green: { bg: '#D1FAE5', text: '#065F46' },
  purple: { bg: '#EDE9FE', text: '#5B21B6' },
  orange: { bg: '#FFEDD5', text: '#C2410C' },
  yellow: { bg: '#FEF3C7', text: '#D97706' },
  grey: { bg: '#F3F4F6', text: '#4B5563' }
};

const colorKeys: TagColor[] = ['blue', 'green', 'purple', 'orange', 'red', 'yellow', 'grey'];

const TagPill: React.FC<TagPillProps> = ({
  color = 'blue',
  autocolor,
  text,
  icon,
  trailingIcon,
  sx,
  hoverContent,
  onClick,
  emphasize = false,
  maxWidth,
  ellipsis = false
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
    if (hoverContent) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleMouseLeave = () => {
    setAnchorEl(null);
  };

  const handleTrailingIconClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (onClick) {
      onClick(event);
    }
  };

  const open = Boolean(anchorEl);

  const resolvedColor = useMemo(() => {
    if (autocolor !== undefined) {
      // Use modulo to cycle through colors
      const index = Math.abs(autocolor) % colorKeys.length;
      return colorKeys[index];
    }
    return color;
  }, [color, autocolor]);

  // Ensure we always have a valid color
  const safeColor: TagColor = resolvedColor || 'blue';
  const { bg, text: textColor } = colorMap[safeColor];


  return (
    <>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleTrailingIconClick}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          backgroundColor: bg,
          color: textColor,
          borderRadius: 4,
          border: emphasize ? `1px solid ${textColor}` : 'none',
          padding: '4px 12px',
          fontSize: '0.875rem',
          fontWeight: emphasize ? 600 : 500,
          cursor: onClick ? 'pointer' : 'default',
          transition: 'all 0.2s ease',
          maxWidth: maxWidth,
          flexShrink: 0,
          minWidth: 0,
          ...((onClick || hoverContent) && {
            '&:hover': {
              filter: 'brightness(0.95)',
            },
          }),
          ...sx
        }}
      >
        {icon && (
          <Box sx={{ mr: 0.75, display: 'flex', alignItems: 'center' }}>
            {icon}
          </Box>
        )}
        <Typography
          sx={{
            whiteSpace: ellipsis ? 'nowrap' : 'nowrap',
            color: 'inherit',
            fontWeight: emphasize ? 700 : 500,
            lineHeight: 1.5,
            fontSize: '0.875rem',
            overflow: ellipsis ? 'hidden' : 'visible',
            textOverflow: ellipsis ? 'ellipsis' : 'clip',
            minWidth: 0,
            flex: ellipsis ? 1 : 'none',
          }}
        >
          {text}
        </Typography>
        {trailingIcon}
      </Stack>

      {hoverContent && (
        <Popover
          open={open}
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          onClose={handleMouseLeave}
          disableRestoreFocus
          sx={{
            pointerEvents: 'none',
            '& .MuiPopover-paper': {
              boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.15)',
              borderRadius: 2,
              mt: 0.5,
              maxWidth: 220
            }
          }}
        >
          <Box sx={{ p: 1.5 }}>
            {hoverContent}
          </Box>
        </Popover>
      )}
    </>
  );
};

export default TagPill; 